import React, { useMemo } from "react";
import { rad, sp } from "../../constants/styles";

// Pure SVG bar chart — no dependencies
function RevenueChart({ pays, h, t, onClick }) {
  const months = useMemo(() => {
    const now = new Date();
    const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const result = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = `${mNames[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`;
      const shortLabel = mNames[d.getMonth()];
      const monthPays = pays.filter(p => p.mo === label);
      const collected = monthPays.filter(p => p.st === "paid").reduce((s, p) => s + p.amt + (p.com || 0), 0);
      const due = monthPays.filter(p => p.st === "due" && !p.pending).reduce((s, p) => s + p.amt + (p.com || 0), 0);
      result.push({ label, shortLabel, collected, due });
    }
    return result;
  }, [pays]);

  const maxVal = Math.max(...months.map(m => m.collected + m.due), 1);
  const chartH = 80;
  const chartW = 280;
  const barW = 28;
  const gap = (chartW - months.length * barW) / (months.length + 1);
  const totalCollected = months.reduce((s, m) => s + m.collected, 0);
  const totalDue = months.reduce((s, m) => s + m.due, 0);
  const currentMonth = months[months.length - 1];

  return (
    <div onClick={onClick} style={{ background: h.cd, borderRadius: rad.lg, padding: `${sp.lg}px ${sp.lg}px ${sp.md}px`, border: `1px solid ${h.bd}`, boxShadow: h.sh, cursor: onClick ? "pointer" : "default", marginBottom: sp.xxl }}>
      {/* Title row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: sp.md }}>
        <div>
          <div style={{ color: h.tx, fontSize: 13, fontWeight: 700, letterSpacing: "-0.1px" }}>{t.revenueChart || "Revenue Trend"}</div>
          <div style={{ color: h.ts, fontSize: 10, marginTop: 2 }}>{t.last6Months || "Last 6 months"}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ color: h.gn, fontSize: 15, fontWeight: 700 }}>₹{totalCollected.toLocaleString()}</div>
          <div style={{ color: h.ts, fontSize: 10 }}>{t.collected || "Collected"}</div>
        </div>
      </div>

      {/* SVG Chart */}
      <svg width="100%" viewBox={`0 0 ${chartW} ${chartH + 20}`} style={{ overflow: "visible" }}>
        {months.map((m, i) => {
          const x = gap + i * (barW + gap);
          const collH = Math.max((m.collected / maxVal) * chartH, m.collected > 0 ? 4 : 0);
          const dueH = Math.max((m.due / maxVal) * chartH, m.due > 0 ? 4 : 0);
          const isCurrentMonth = i === months.length - 1;
          return (
            <g key={m.label}>
              {/* Due bar (behind) */}
              {dueH > 0 && <rect
                x={x} y={chartH - dueH} width={barW} height={dueH}
                rx="4" fill={`${h.rd}25`}
              />}
              {/* Collected bar */}
              {collH > 0 && <rect
                x={x} y={chartH - collH} width={barW} height={collH}
                rx="4" fill={isCurrentMonth ? h.ac : `${h.ac}70`}
              />}
              {/* Empty state bar */}
              {collH === 0 && dueH === 0 && <rect
                x={x} y={chartH - 3} width={barW} height={3}
                rx="2" fill={`${h.bd}`}
              />}
              {/* Month label */}
              <text
                x={x + barW / 2} y={chartH + 14}
                textAnchor="middle" fontSize="9"
                fill={isCurrentMonth ? h.ac : (h.ts || h.sc)}
                fontWeight={isCurrentMonth ? "700" : "400"}
                fontFamily="Inter, -apple-system, sans-serif"
              >{m.shortLabel}</text>
            </g>
          );
        })}
        {/* Baseline */}
        <line x1="0" y1={chartH} x2={chartW} y2={chartH} stroke={h.bd} strokeWidth="1" />
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", gap: sp.xl, marginTop: sp.xs }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.xs + 2 }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: h.ac }} />
          <span style={{ color: h.ts, fontSize: 10 }}>{t.collected || "Collected"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: sp.xs + 2 }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: `${h.rd}40` }} />
          <span style={{ color: h.ts, fontSize: 10 }}>{t.due || "Due"}</span>
        </div>
        {totalDue > 0 && <div style={{ marginLeft: "auto", color: h.rd, fontSize: 10, fontWeight: 600 }}>
          ₹{totalDue.toLocaleString()} {t.pending || "pending"}
        </div>}
      </div>

      {/* This month callout */}
      {(currentMonth.collected > 0 || currentMonth.due > 0) && <div style={{ marginTop: sp.md, background: `${h.ac}08`, borderRadius: rad.sm + 2, padding: `${sp.sm}px ${sp.md}px`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: h.ts, fontSize: 11 }}>{t.thisMonth || "This month"}</span>
        <div style={{ display: "flex", gap: sp.md }}>
          {currentMonth.collected > 0 && <span style={{ color: h.gn, fontWeight: 600, fontSize: 11 }}>✓ ₹{currentMonth.collected.toLocaleString()}</span>}
          {currentMonth.due > 0 && <span style={{ color: h.rd, fontWeight: 600, fontSize: 11 }}>! ₹{currentMonth.due.toLocaleString()}</span>}
        </div>
      </div>}
    </div>
  );
}

export default RevenueChart;
